import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  Polyline,
  Tooltip as LeafletTooltip,
} from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import L from "leaflet";
import { Spin, Button, Tooltip as AntdTooltip } from "antd";
import "leaflet.heat";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "../../components/MapComponent/MapComponent.scss";
import ReactDOMServer from "react-dom/server";
import medic from "../../../Assets/Images/medic.png";
import clinic from "../../../Assets/Images/medical.png";
import community from "../../../Assets/Images/collaboration.png";
import communityCenter from "../../../Assets/Images/roommate.png";
import others from "../../../Assets/Images/others.png";
import {
  FileExcelOutlined,
  FileImageOutlined,
  FileJpgOutlined,
  FilePdfOutlined,
  HeatMapOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Filters from "./Filters";
import CryptoJS from "crypto-js";
import { formatDate } from "../../../services/helpFunctions/formatDateAndTime";
import axios from "axios";
import domtoimage from "dom-to-image";

const createIcon = (color, facilityName) => {
  const defaultIcon = new L.DivIcon({
    html: `<div style="background-color:#999; width:24px; height:24px; border-radius:50%;"></div>`,
    className: "custom-default-icon",
    iconSize: [24, 24],
  });

  let iconHtml = "";

  switch (facilityName) {
    case "Hospital":
      iconHtml = `<img src="${medic}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "Clinic":
      iconHtml = `<img src="${clinic}" style="width: 25px; height: 25px;" alt="Hospital Icon" />`;
      break;
    case "Community":
      iconHtml = `<img src="${community}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "Community Centers":
      iconHtml = `<img src="${communityCenter}" style="width: 25px; height: 25px;" alt="Hospital Icon" />`;
      break;
    case "Others":
      iconHtml = `<img src="${others}" style="width: 30px; height: 30px;" alt="Hospital Icon" />`;
      break;
    case "noFacility":
      iconHtml = ReactDOMServer.renderToString(
        <QuestionCircleOutlined
          style={{ fontSize: 20, color: "transparent" }}
        />
      );
      break;
    default:
      return defaultIcon;
  }

  const svgIcon = `
  <div style="position: relative; width: 40px; height: 60px;">
    <div style="position: absolute; top: 0; left: 0; z-index: 1;">
      <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 0C9 0 0 9 0 20c0 11 20 40 20 40s20-29 20-40C40 9 31 0 20 0z" fill="${color}" />
      </svg>
    </div>
    <div style="position: absolute; top: 5px; left: 5px; z-index: 2; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">
      ${iconHtml}
    </div>
  </div>
`;

  return L.divIcon({
    className: "custom-pin-icon",
    html: svgIcon,
    iconSize: [40, 60],
    iconAnchor: [20, 60],
    popupAnchor: [0, -60],
  });
};

function HeatmapLayer({ points, visible }) {
  const map = useMapEvents({});
  useEffect(() => {
    if (!map || !visible) return;
    const heatPoints = points
      .map((p) => {
        if (p?.latitude && p?.longitude) {
          return [p.latitude, p.longitude, 2];
        }
        return null;
      })
      .filter(Boolean);

    const heatLayer = L.heatLayer(heatPoints, {
      radius: 30,
      blur: 20,
      maxZoom: 8,
      gradient: {
        0.4: "blue",
        0.65: "lime",
        1: "red",
      },
    });

    heatLayer.addTo(map);
    return () => map.removeLayer(heatLayer);
  }, [map, points, visible]);

  return null;
}

const statusColors = {
  2: "#325899CC",
  3: "#964B00CC",
  4: "#3B81FECC",
  5: "#045B46CC",
  6: "#0F7507CC",
};

const availableStatuses = [2, 3, 4, 5, 6];

const initialFilters = {
  Search: "",
  SiteVisitStatus: availableStatuses,
  Country: "",
  State: "",
  FacilityIds: [],
};

const baseUrl = process.env.REACT_APP_API_BASE_URL.endsWith("/")
  ? process.env.REACT_APP_API_BASE_URL
  : `${process.env.REACT_APP_API_BASE_URL}/`;

const PublicMap = () => {
  const navigate = useNavigate();
  const [exportedData, setExportedData] = useState({});
  const [siteVisits, setSiteVisits] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showDistances, setShowDistances] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [distance, setDistance] = useState(false);

  const handleToggle = (checked) => {
    setDistance(checked);
  };

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const encrypted = params.get("map");
    const secret = "K4JW7-RRJM7-VTJJY-JV899-QHVYM";

    if (encrypted) {
      try {
        const bytes = CryptoJS.AES.decrypt(encrypted, secret);
        const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        setExportedData(decrypted);
      } catch (err) {
        navigate("/");
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (exportedData?.projectId) {
      fetchSiteVisits(exportedData.projectId);
    }
  }, [exportedData, filters]);

  const fetchSiteVisits = async (projectId) => {
    setLoading(true);

    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.append(key, value);
      }
    });

    try {
      const response = await axios.get(
        `${baseUrl}api/Site/getSiteVisitForMap/${projectId}?${params.toString()}`
      );
      setSiteVisits(response.data.data ?? []);
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportMap = async (format) => {
    if (!mapContainerRef.current) return;

    domtoimage
      .toPng(mapContainerRef.current)
      .then((dataUrl) => {
        if (format === "png") {
          const link = document.createElement("a");
          link.download = "map.jpeg";
          link.href = dataUrl;
          link.click();
        } else {
          const pdf = new jsPDF("portrait", "pt", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          const img = new Image();
          img.src = dataUrl;

          img.onload = () => {
            const imgWidth = img.width;
            const imgHeight = img.height;

            // Calculate scale to fit image inside PDF page with some margin
            const margin = 20;
            const maxWidth = pdfWidth - 2 * margin;
            const maxHeight = pdfHeight - 2 * margin;

            let renderWidth = imgWidth;
            let renderHeight = imgHeight;

            // Scale image proportionally to fit in PDF page
            if (imgWidth > maxWidth || imgHeight > maxHeight) {
              const widthRatio = maxWidth / imgWidth;
              const heightRatio = maxHeight / imgHeight;
              const scale = Math.min(widthRatio, heightRatio);
              renderWidth = imgWidth * scale;
              renderHeight = imgHeight * scale;
            }

            // Add image to PDF centered with margin
            const x = (pdfWidth - renderWidth) / 2;
            const y = (pdfHeight - renderHeight) / 2;

            pdf.addImage(dataUrl, "png", x, y, renderWidth, renderHeight);
            pdf.save("map.pdf");
          };
        }
      })
      .catch((error) => {
        console.error("Error generating PDF:", error);
      });
  };

  const exportMapToExcel = () => {
    const projectId = exportedData?.projectId;

    if (projectId) {
      const params = new URLSearchParams();

      if (filters?.Country) params.append("Country", filters.Country);
      if (filters?.State) params.append("State", filters.State);
      if (filters?.Search) params.append("Name", filters.Search);
      if (filters?.FacilityIds?.length)
        filters.FacilityIds.forEach((id) => params.append("FacilityIds", id));
      if (filters?.SiteVisitStatus?.length)
        filters.SiteVisitStatus.forEach((status) =>
          params.append("SiteVisitStatus", status)
        );

      const url = `${baseUrl}api/Site/exportSiteVisitMap?projectId=${projectId}&${params.toString()}`;

      axios
        .post(url, null, {
          responseType: "blob",
        })
        .then((res) => {
          const blob = new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "SiteVisitMap.xlsx");
          document.body.appendChild(link);
          link.click();

          link.remove();
          window.URL.revokeObjectURL(url);
        });
      // .catch((err) => console.log(err));
    }
  };

  return (
    <div
      style={{
        width: "100svw",
        height: "100svh",
        position: "fixed",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: 20,
        boxSizing: "border-box",
        zIndex: 1000,
        backgroundColor: "white",
      }}
      ref={mapContainerRef}
    >
      <div className="d-flex flex-row gap-5 m-2">
        <h2 className="exportedMapTitle">
          Contract: <span>{siteVisits?.[0]?.contractName}</span>
        </h2>
        <h2 className="exportedMapTitle">
          Round: <span>{siteVisits?.[0]?.projectName}</span>
        </h2>

        <h2 className="exportedMapTitle">
          Published Map Date:{" "}
          <span>{formatDate(exportedData?.publicDate)}</span>
        </h2>
      </div>
      <Filters initialFilters={initialFilters} setFilters={setFilters} />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 10,
          gap: 10,
        }}
      >
        <AntdTooltip title="Toggle Heatmap">
          <Button
            type={showHeatmap ? "primary" : "default"}
            icon={<HeatMapOutlined />}
            onClick={() => {
              setShowHeatmap((prev) => {
                if (!prev) setShowDistances(false);
                return !prev;
              });
            }}
          />
        </AntdTooltip>
        <AntdTooltip title="Toggle Distances">
          <Button
            type={showDistances ? "primary" : "default"}
            icon={<SwapOutlined />}
            onClick={() => {
              setShowDistances((prev) => {
                if (!prev) setShowHeatmap(false);
                return !prev;
              });
            }}
          />
        </AntdTooltip>

        <AntdTooltip title="Export as PNG">
          <Button
            icon={<FileImageOutlined />}
            onClick={() => exportMap("png")}
          />
        </AntdTooltip>
        <AntdTooltip title="Export as PDF">
          <Button icon={<FilePdfOutlined />} onClick={() => exportMap("pdf")} />
        </AntdTooltip>

        <AntdTooltip placement="topRight" title="Export as Excel">
          <Button icon={<FileExcelOutlined />} onClick={exportMapToExcel} />
        </AntdTooltip>
      </div>

      <div style={{ height: "100%", width: "100%", border: "1px solid #ddd" }}>
        {loading ? (
          <Spin
            size="large"
            className="d-flex w-100 h-100 align-items-center justify-content-center"
          />
        ) : (
          <MapContainer
            whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
            minZoom={1}
            center={[20, 0]}
            zoom={2}
            style={{ height: "100%", width: "100%" }}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              crossOrigin="anonymous"
            />

            {!showHeatmap && (
              <MarkerClusterGroup chunkedLoading>
                {siteVisits?.map((item, idx) => (
                  <Marker
                    key={idx}
                    position={[item?.latitude || 0, item?.longitude || 0]}
                    icon={createIcon(
                      statusColors[item.siteVisitStatus] || "#d4217766",
                      item?.facilityName || "noFacility"
                    )}
                  >
                    <LeafletTooltip
                      direction="top"
                      offset={[0, -30]}
                      opacity={1}
                    >
                      {item.siteVisitIdentifier || item.name || "Site Visit"}
                    </LeafletTooltip>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}

            {showHeatmap && (
              <HeatmapLayer points={siteVisits} visible={showHeatmap} />
            )}

            {showDistances &&
              siteVisits?.length > 1 &&
              (() => {
                const validCoords = siteVisits
                  ?.filter((loc) => loc?.latitude && loc?.longitude)
                  .map((loc) => [loc.latitude, loc.longitude]);

                return validCoords.map((coord, index) => {
                  if (index === validCoords.length - 1) return null;

                  const from = L.latLng(coord);
                  const to = L.latLng(validCoords[index + 1]);
                  const distanceMeters = from.distanceTo(to);

                  if (distanceMeters < 100) return null;

                  const midLat = (coord[0] + validCoords[index + 1][0]) / 2;
                  const midLng = (coord[1] + validCoords[index + 1][1]) / 2;

                  const displayDistance =
                    distanceMeters > 1000
                      ? `${(distanceMeters / 1000).toFixed(2)} km`
                      : `${Math.round(distanceMeters)} m`;

                  return (
                    <React.Fragment key={index}>
                      <Polyline
                        positions={[coord, validCoords[index + 1]]}
                        pathOptions={{ color: "black" }}
                      />
                      <Marker
                        position={[midLat, midLng]}
                        icon={L.divIcon({
                          className: "distance-label",
                          html: `<div>${displayDistance}</div>`,
                        })}
                        interactive={false}
                      />
                    </React.Fragment>
                  );
                });
              })()}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default PublicMap;
